import React, { ReactElement } from 'react';
import {
  Box,
  Button,
  IconButton,
  Link,
  styled,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridCallbackDetails,
  GridCellParams,
  GridSelectionModel,
} from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import { Driver } from '../../../data/models/driver.model';
import StatusIndicator from '../../StatusIndicator';
import { useNavigate } from 'react-router-dom';
import { FULLSTORY_MASK_CLASSNAME } from '../../../services/analyticsUtils';

const PREFIX = 'OnboardingDriverDataTable';

const classes = {
  container: `${PREFIX}-container`,
  grid: `${PREFIX}-grid`,
  titlebar: `${PREFIX}-titlebar`,
};

const StyledBox = styled('div')(({ theme }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    marginTop: theme.spacing(3),
  },
  [`& .${classes.grid}`]: {
    '&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus':
      {
        outline: 'none',
      },
  },
  [`& .${classes.titlebar}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
  },
}));

interface OnboardingDriverDataTableProps {
  drivers: Driver[];
  loading: boolean;
  onDelete?: (
    evt: any,
    id: Driver,
    firstName: string,
    lastName: string,
  ) => void;
  onSendActivationEmails?: (ids: any[], error: boolean) => void;
  onSendActivationEmail?: (driver: any) => void;
  onClickEditButton?: (driver: any) => void;
}

export default function OnboardingDriverDataTable({
  drivers,
  loading,
  onDelete,
  onSendActivationEmails,
  onSendActivationEmail,
  onClickEditButton,
}: OnboardingDriverDataTableProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
  const renderDriverLastName = (params: GridCellParams): ReactElement => {
    return (
      <Link
        href="#"
        onClick={(evt: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
          evt.preventDefault();
          navigate('/drivers/' + params.row.driverID);
        }}
        color="textPrimary"
        underline="hover">
        {params.row.driverLastName}
      </Link>
    );
  };
  const columns = [
    { field: 'driverID', headerName: 'ID', hide: true },
    {
      field: 'driverLastName',
      headerName: t('users.lastName'),
      flex: 1,
      renderCell: renderDriverLastName,
      cellClassName: FULLSTORY_MASK_CLASSNAME,
    },
    {
      field: 'driverFirstName',
      headerName: t('users.firstName'),
      flex: 1,
      sortable: false,
      cellClassName: FULLSTORY_MASK_CLASSNAME,
    },
    {
      field: 'emailAddresses',
      headerName: t('users.email'),
      flex: 2,
      sortable: false,
      cellClassName: FULLSTORY_MASK_CLASSNAME,
      renderCell: (params: GridCellParams) => params.row.emailAddresses[0],
    },
    // {
    //   field: 'preferredUserName',
    //   headerName: 'Username',
    //   flex: 1,
    //   sortable: false,
    // },
    {
      field: 'driverState',
      headerName: t('common.active'),
      width: 80,
      renderCell: (params: GridCellParams) => <StatusIndicator cell={params} />,
    },
    {
      field: 'Actions',
      headerName: ' ',
      width: 150,
      hide: !onDelete,
      renderCell: (params: GridCellParams) => {
        return (
          <>
            {onSendActivationEmail && (
              <Tooltip title={t('drivers.rensendActivation')}>
                <IconButton
                  data-testid="reset"
                  onClick={() => onSendActivationEmail(params.row)}
                  size="large"
                  color="default">
                  <ForwardToInboxIcon />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title={t('drivers.deleteDriver')}>
                <IconButton
                  data-testid="delete"
                  onClick={(evt) =>
                    onDelete(
                      evt,
                      params.row,
                      params.row.DriverFirstName,
                      params.row.DriverLastName,
                    )
                  }
                  size="large"
                  color="primary">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}

            {onClickEditButton && (
              <Tooltip title={t('drivers.edit')}>
                <IconButton
                  data-testid="edit"
                  onClick={(evt) => onClickEditButton(params.row)}
                  size="large"
                  color="default">
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
      sortable: false,
    },
  ];

  const onSelectionChange = (
    selectionModel: GridSelectionModel,
    details: GridCallbackDetails,
  ) => {
    setSelectedIds(selectionModel as number[]);
  };

  const sendActivationEmails = () => {
    const getFiltered: any = drivers.filter((driver) =>
      selectedIds.includes(driver.driverID ?? 0),
    );
    const checkNoUsername =
      getFiltered &&
      getFiltered.filter(
        (driver: Driver) =>
          driver.preferredUserName === '' ||
          driver.preferredUserName === undefined,
      );
    const driversGet = drivers.filter((driver: any) =>
      selectedIds.includes(driver?.driverID),
    );
    if (onSendActivationEmails) {
      setSelectedIds([]);

      onSendActivationEmails(driversGet, checkNoUsername.length > 0);
    }
  };

  return (
    <StyledBox data-testid="onboarding-table" className={classes.container}>
      {drivers ? (
        <StyledBox>
          <Box component="div" className={classes.titlebar}>
            <Typography variant="h5" align="center">
              {t('drivers.onboardingDrivers')}
            </Typography>
            {onSendActivationEmail && (
              <Button
                color="primary"
                variant="contained"
                disabled={selectedIds.length === 0}
                onClick={sendActivationEmails}>
                {t('users.sendActivation')}
              </Button>
            )}
          </Box>
          <DataGrid
            disableSelectionOnClick
            getRowId={(row) => row.driverID!}
            rows={drivers}
            columns={columns}
            rowCount={drivers.length}
            hideFooter={true}
            autoHeight={true}
            loading={loading}
            className={classes.grid}
            checkboxSelection={!!onSendActivationEmail}
            selectionModel={selectedIds}
            disableColumnMenu={true}
            disableColumnSelector={true}
            onSelectionModelChange={onSelectionChange}
          />
        </StyledBox>
      ) : null}
    </StyledBox>
  );
}
